<template>
  <div>
    <div class="vacancy-list-filter">
      <div class="vacancy-list-filter__title">
        {{ $t('filterTitle') }}
      </div>
      <VRadioGroup v-model="filter.type">
        <VRadio :value="vacancyListFilterType.ACTIVE" class="vacancy-list-filter__item">
          <template #label>
            <div>
              <span class="vacancy-list-filter__label">{{ $t('showActive') }}</span>
            </div>
          </template>
        </VRadio>
        <div v-if="filter.type === vacancyListFilterType.ACTIVE" class="vacancy-list-filter__description">
          {{ $t('showActiveDescription') }}
        </div>
        <VRadio :value="vacancyListFilterType.ADDRESS" class="vacancy-list-filter__item">
          <template #label>
            <span class="vacancy-list-filter__label">{{ $t('showAddress') }}</span>
          </template>
        </VRadio>
        <div v-if="filter.type === vacancyListFilterType.ADDRESS" class="vacancy-list-filter__description">
          {{ $t('showAddressDescription') }}
        </div>
        <VacanciesListDetailFilterAddress v-model="filter.ids" v-if="filter.type === vacancyListFilterType.ADDRESS" />
        <VRadio :value="vacancyListFilterType.LIST" class="vacancy-list-filter__item">
          <template #label>
            <span class="vacancy-list-filter__label">{{ $t('showList') }}</span>
          </template>
        </VRadio>
        <div v-if="filter.type === vacancyListFilterType.LIST" class="vacancy-list-filter__description">
          {{ $t('showListDescription') }}
        </div>
        <VacanciesListDetailFilterList v-model="filter.ids" v-if="filter.type === vacancyListFilterType.LIST" />
      </VRadioGroup>
    </div>
    <slot />
    <VacanciesListDetailList :value="filterVacancies" @excluded="onExclude" v-if="showVacancies" />
  </div>
</template>

<script>
import { vacancyListFilterType } from '@/enums';
import {
  computed, ref, toRef, toRefs, watch,
} from '@vue/composition-api';
import VacanciesListDetailFilterAddress from '@/components/VacancyLists/VacanciesListDetailFilterAddress';
import VacanciesListDetailFilterList from '@/components/VacancyLists/VacanciesListDetailFilterList';
import VacanciesListDetailList from '@/components/VacancyLists/VacanciesListDetailList';

export default {
  name: 'VacanciesListsDetailFilter',
  components: { VacanciesListDetailList, VacanciesListDetailFilterList, VacanciesListDetailFilterAddress },
  model: { prop: 'filter' },
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { filter } = toRefs(props);

    const showVacanciesForAddress = computed(
      () => filter.value.type === vacancyListFilterType.ADDRESS && (
        filter.value.ids && filter.value.ids.length !== 0
      ),
    );

    const showVacanciesForAll = computed(
      () => filter.value.type === vacancyListFilterType.ACTIVE,
    );

    const showVacancies = computed(
      () => showVacanciesForAll.value || showVacanciesForAddress.value,
    );

    watch(() => filter.value.type, () => {
      if (filter.value.type === vacancyListFilterType.ACTIVE) {
        filter.value.ids = [];
      }
      if (filter.value.type === vacancyListFilterType.ADDRESS
        || filter.value.type === vacancyListFilterType.LIST) {
        filter.value.ids = [];
      }
    });

    function onExclude(id) {
      filter.value.excludedIds.push(id);
    }

    const filterVacancies = computed(() => {
      switch (filter.value.type) {
        case vacancyListFilterType.ADDRESS:
          return {
            addressIds: filter.value.ids,
            excludedIds: filter.value.excludedIds.value,
          };
        case vacancyListFilterType.ACTIVE:
          return {
            excludedIds: filter.value.excludedIds.value,
          };
        default:
          return {};
      }
    });

    return {
      vacancyListFilterType,
      filterVacancies,
      onExclude,
      showVacancies,
    };
  },
};
</script>

<style lang="scss" scoped>
  .vacancy-list-filter {
    border: 1px dashed rgba(0, 0, 0, 0.16);
    border-radius: 0.625rem;
    padding: 1.875rem 2.5rem 0.5rem;
    margin-bottom: 1.25rem;
    &__title {
      font-size: 1.125rem;
      line-height: 1.1;
      margin-bottom: 1.25rem;
    }
    &__item {
      padding-bottom: 0.25rem;
    }
    &__label {
      color: #212121;
    }
    &__description {
      font-size: 0.8125rem;
      line-height: 1.2;
      color: #757575;
      padding-left: 2rem;
      margin: -0.5rem 0 0.75rem;
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .vacancy-list-filter {
      padding: 1.5rem 1.5rem 0.25rem;
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .vacancy-list-filter {
      padding: 1.375rem 1rem 0;
      &__title {
        font-size: 1rem;
        margin-bottom: 1rem;
      }
      &__item {
        padding-bottom: 0;
      }
      &__label {
        font-size: 0.875rem;
      }
    }
  }

</style>

<i18n lang="yml" locale="ru">
  filterTitle: Фильтр
  showActive: Все вакансии
  showActiveDescription: В подборку будут попадать все опубликованные вакансии.
  showAddress: Вакансии по адресу
  showAddressDescription: Если в вашей компании есть вакансии по всей стране, но вы хотите сделать подборку для определённого местоположения.
  showList: Выбрать вручную
  showListDescription: Подойдёт, если у вас размещено много вакансий на Huntica, но вы хотите сделать подборку, например, для линейного персонала. Убедитесь, что у всех  выбранных вакансий установлен длительный период публикации, так как если вакансия перейдёт в архив, то кандидаты её не увидят. Рекомендуется для позиций, на которые открыт постоянный набор.
</i18n>
<i18n lang="yml" locale="en">
filterTitle: Filter
showActive: All jobs
showActiveDescription: The selection will include all published jobs.
showAddress: By address
showAddressDescription: If your company has jobs nationwide, but you want to make a selection for a specific location.
showList: Custom selection
showListDescription: Suitable if you have a lot of jobs posted on Huntica, but you want to make a specific selection, for example, line personnel. Make sure that all selected jobs have a long expiration period because if the job goes to the archive, candidates will not see it. Recommended for permanent hiring positions.
</i18n>
